import React, { useState, useEffect } from 'react';
import { resolveImageUrl } from '../../utils/firebaseImageUtils';

const PlaceholderImage = ({ src, alt, className, fallbackName }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        // Try to resolve the image URL from Firebase Storage
        const resolvedUrl = await resolveImageUrl(src, fallbackName || alt);
        
        if (resolvedUrl) {
          setImageUrl(resolvedUrl);
        } else {
          setHasError(true);
        }
      } catch (error) {
        console.warn('Error loading image:', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    // Only attempt to load if we have a source
    if (src) {
      loadImage();
    } else {
      setIsLoading(false);
      setHasError(true);
    }
  }, [src, alt, fallbackName]);

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className={`placeholder-image loading ${className || ''}`}>
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        {alt && <span className="sr-only">{alt} - Loading</span>}
      </div>
    );
  }

  // Show actual image if loaded successfully
  if (imageUrl && !hasError) {
    return (
      <img
        src={imageUrl}
        alt={alt}
        className={className}
        onError={handleImageError}
        onLoad={handleImageLoad}
      />
    );
  }

  // Show placeholder SVG if no image or error
  return (
    <div className={`placeholder-image ${className || ''}`}>
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 400 300" 
        width="100%" 
        height="100%"
        preserveAspectRatio="xMidYMid meet"
      >
        <rect width="100%" height="100%" fill="#f8f9fa" />
        <g fill="#dee2e6">
          <polygon points="150,150 250,250 50,250" />
          <polygon points="200,120 300,250 100,250" />
          <circle cx="300" cy="100" r="30" />
          <circle cx="80" cy="80" r="20" />
        </g>
        <text 
          x="200" 
          y="200" 
          textAnchor="middle" 
          fill="#6c757d" 
          fontSize="14" 
          fontFamily="Arial, sans-serif"
        >
          {alt ? alt.substring(0, 20) + (alt.length > 20 ? '...' : '') : 'No Image'}
        </text>
      </svg>
      {alt && <span className="sr-only">{alt}</span>}
    </div>
  );
};

export default PlaceholderImage;
